import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@finitless/shared/lib";
import { isEndpointEnabled } from "@finitless/shared/config";
import { buildQueryParams } from "../lib/globalUtils";

/**
 * Default parameters to send with AI agents requests
 */
const aiAgentsParamsToSend = {
  params:
    "id,provider,provider_type,provider_external_id,provider_settings,enabled,created_at,updated_at,franchise_id,metrics,configured,misconfigured_reason,site.id,site.domain,site.temporal_domain,site.ssl_status,site.ssl_process_status,site.ssl_error_type",
};

const getAgentSiteQueryKey = (agentId) => ["ai-agent-site", agentId];

const mergeSiteData = (currentSite, incomingSite) => {
  const safeCurrent = currentSite || {};
  const safeIncoming = incomingSite || {};

  return {
    ...safeCurrent,
    ...safeIncoming,
    deployment_provider_settings: {
      ...(safeCurrent.deployment_provider_settings || {}),
      ...(safeIncoming.deployment_provider_settings || {}),
    },
  };
};

const syncAgentSiteCache = (queryClient, agentId, siteData) => {
  if (!agentId || !siteData) return;

  queryClient.setQueryData(getAgentSiteQueryKey(agentId), (oldSite) =>
    mergeSiteData(oldSite, siteData),
  );

  queryClient.setQueriesData({ queryKey: ["ai-agents"] }, (oldData) => {
    if (!oldData?.agents) return oldData;

    return {
      ...oldData,
      agents: oldData.agents.map((agent) => {
        if (agent.id !== agentId) return agent;

        return {
          ...agent,
          site: mergeSiteData(agent.site, siteData),
        };
      }),
    };
  });
};

/**
 * Refetch a single agent and update it in the agents list cache.
 * Extracts the repeated fetch-and-merge pattern used across WhatsApp OAuth mutations.
 */
const refetchAndUpdateAgent = async (queryClient, agentId, label) => {
  const agentData = await queryClient.fetchQuery({
    queryKey: ["ai-agent", agentId, label, Date.now()],
    queryFn: async () => {
      const response = await apiClient.get(
        `/ai/agents/${agentId}?params=${aiAgentsParamsToSend.params}`,
      );
      return response?.result;
    },
    staleTime: 0,
    gcTime: 0,
  });

  if (agentData) {
    queryClient.setQueriesData({ queryKey: ["ai-agents"] }, (oldData) => {
      if (!oldData?.agents) return oldData;
      return {
        ...oldData,
        agents: oldData.agents.map((agent) =>
          agent.id === agentId ? { ...agent, ...agentData } : agent,
        ),
      };
    });
  }
};

/**
 * Hook to get AI agents with filtering and pagination
 * @param {Object} params - Query parameters
 * @param {string} params.enabledFilter - Filter by enabled status ('all', 'enabled', 'disabled')
 * @param {string} params.providerFilter - Filter by provider type ('all', 'whatsapp', 'phone', 'web')
 * @param {number} params.page - Page number (default: 1); ignored when includePagination is false
 * @param {number} params.pageSize - Page size (default: 10); ignored when includePagination is false
 * @param {boolean} [params.includePagination=true] - When false, `page` and `page_size` are not sent to the API.
 * @returns {Object} Query result with agents and pagination
 */
export const useAiAgents = (params = {}) => {
  const {
    enabledFilter = "all",
    providerFilter = "all",
    page = 1,
    pageSize = 10,
    includePagination = true,
  } = params;

  const whereConditions = [];

  // Add enabled filter condition
  if (enabledFilter === "enabled" || enabledFilter === "disabled") {
    whereConditions.push({
      attribute: "enabled",
      value: enabledFilter === "enabled",
    });
  }

  // Add provider filter condition
  if (providerFilter !== "all") {
    whereConditions.push({
      attribute: "provider",
      value: providerFilter,
    });
  }

  const whereClause = whereConditions.length > 0 ? whereConditions : undefined;

  const queryParams = {
    ...aiAgentsParamsToSend,
    where: whereClause,
    ...(includePagination ? { page, page_size: pageSize } : {}),
    orderBy: "-created_at",
  };

  const queryString = buildQueryParams(queryParams);

  return useQuery({
    queryKey: includePagination
      ? ["ai-agents", enabledFilter, providerFilter, page, pageSize]
      : ["ai-agents", enabledFilter, providerFilter, "all"],
    queryFn: async () => {
      const response = await apiClient.get(
        `/ai/agents${queryString ? `?${queryString}` : ""}`,
      );
      return {
        agents: response?.result || [],
        pagination: response?.pagination || {},
      };
    },
    enabled: isEndpointEnabled("ai-agents", "list"),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false,
  });
};

/**
 * Hook to get a specific AI agent by ID
 * @param {number} agentId - Agent ID
 * @param {string} params - Comma-separated list of fields to include
 * @returns {Object} Query result with agent data
 */
export const useAiAgent = (
  agentId,
  params = "id,provider,provider_type,provider_external_id,provider_settings,enabled,created_at,updated_at,franchise",
) => {
  return useQuery({
    queryKey: ["ai-agent", agentId, params],
    queryFn: async () => {
      const response = await apiClient.get(
        `/ai/agents/${agentId}?params=${params}`,
      );
      return response?.result;
    },
    enabled: isEndpointEnabled("ai-agents", "get") && !!agentId,
    staleTime: 5 * 60 * 1000,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false,
  });
};

/**
 * Hook to get provider settings schemas
 * @returns {Object} Query result with provider schemas
 */
export const useProviderSettingsSchemas = () => {
  return useQuery({
    queryKey: ["ai-agents", "provider-schemas"],
    queryFn: async () => {
      const response = await apiClient.get("/ai/agents/provider_settings");
      return response?.result || {};
    },
    enabled: isEndpointEnabled("ai-agents", "schemas"),
    staleTime: 30 * 60 * 1000, // 30 minutes - schemas don't change often
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false,
  });
};

/**
 * Hook to get available providers and their types
 * @returns {Object} Query result with available providers and types
 */
export const useAvailableProviders = () => {
  const { data: schemas, ...rest } = useProviderSettingsSchemas();

  const availableProviders = React.useMemo(() => {
    if (!schemas) return [];

    return Object.entries(schemas).map(([provider, types]) => {
      const providerTypes = Object.entries(types).map(([type, schema]) => ({
        type,
        hasSettings:
          schema.properties && Object.keys(schema.properties).length > 0,
        schema,
        title: schema.title || `${provider} ${type}`,
        description:
          schema.description || `Settings for ${provider} ${type} integration`,
      }));

      return {
        provider,
        types: providerTypes,
        hasAnySettings: providerTypes.some((t) => t.hasSettings),
      };
    });
  }, [schemas]);

  return {
    data: availableProviders,
    ...rest,
  };
};

/**
 * Hook to get schema for a specific provider and type
 * @param {string} provider - Provider name (e.g., 'whatsapp')
 * @param {string} type - Provider type (e.g., 'chat')
 * @returns {Object} Query result with specific schema
 */
export const useProviderSchema = (provider, type) => {
  const { data: schemas, ...rest } = useProviderSettingsSchemas();

  const schema = React.useMemo(() => {
    if (!schemas || !provider || !type) return null;

    const providerSchema = schemas[provider];
    if (!providerSchema) return null;

    const typeSchema = providerSchema[type];
    if (!typeSchema) return null;

    return {
      ...typeSchema,
      hasSettings:
        typeSchema.properties && Object.keys(typeSchema.properties).length > 0,
    };
  }, [schemas, provider, type]);

  return {
    data: schema,
    ...rest,
  };
};

/**
 * Hook to create a new AI agent
 * @returns {Object} Mutation result
 */
export const useCreateAiAgent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (agentData) => {
      const response = await apiClient.post("/ai/agents", agentData);
      return response?.result;
    },
    onSuccess: () => {
      // Invalidate and refetch agents list
      queryClient.invalidateQueries({ queryKey: ["ai-agents"] });
    },
  });
};

/**
 * Hook to update an AI agent
 * @returns {Object} Mutation result
 */
export const useUpdateAiAgent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ agentId, ...updateData }) => {
      const response = await apiClient.put(`/ai/agents/${agentId}`, updateData);
      return response?.result;
    },
    onSuccess: (data, variables) => {
      // Invalidate and refetch agents list
      queryClient.invalidateQueries({ queryKey: ["ai-agents"] });
      // Invalidate specific agent
      queryClient.invalidateQueries({
        queryKey: ["ai-agent", variables.agentId],
      });
    },
  });
};

/**
 * Hook to get current conversation for an agent
 * @param {string} providerExternalId - Provider external ID
 * @returns {Object} Query result with conversation data
 */
export const useAgentCurrentConversation = (providerExternalId) => {
  return useQuery({
    queryKey: ["ai-agent-conversation", providerExternalId],
    queryFn: async () => {
      const response = await apiClient.get(
        `/ai/agents/${providerExternalId}/conversations/current`,
      );
      return response?.result;
    },
    enabled:
      isEndpointEnabled("ai-conversations", "current") && !!providerExternalId,
    staleTime: 1 * 60 * 1000, // 1 minute - conversations change frequently
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false,
  });
};

/**
 * Hook to get messages from current conversation
 * @param {string} providerExternalId - Provider external ID
 * @returns {Object} Query result with messages data
 */
export const useAgentConversationMessages = (providerExternalId) => {
  return useQuery({
    queryKey: ["ai-agent-messages", providerExternalId],
    queryFn: async () => {
      const response = await apiClient.get(
        `/ai/agents/${providerExternalId}/conversations/current/messages`,
      );
      return response?.result || [];
    },
    enabled:
      isEndpointEnabled("ai-conversations", "messages") && !!providerExternalId,
    staleTime: 30 * 1000, // 30 seconds - messages change frequently
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false,
  });
};

/**
 * Hook to get all conversations with pagination
 * @param {Object} params - Query parameters
 * @returns {Object} Query result with conversations and pagination
 */
export const useAiConversations = (params = {}) => {
  const {
    page = 1,
    pageSize = 10,
    statusFilter = "all",
    agentFilter = "all",
  } = params;

  const whereConditions = [];

  // Add status filter condition
  if (statusFilter !== "all") {
    whereConditions.push({
      attribute: "status",
      value: statusFilter,
    });
  }

  // Add agent filter condition
  if (agentFilter !== "all") {
    whereConditions.push({
      attribute: "agent_id",
      value: parseInt(agentFilter),
    });
  }

  const whereClause = whereConditions.length > 0 ? whereConditions : undefined;

  const queryParams = {
    params:
      "id,status,type,user,agent,messages_count,created_at,updated_at,last_message_at",
    where: whereClause,
    page,
    page_size: pageSize,
    orderBy: "-last_message_at",
  };

  const queryString = buildQueryParams(queryParams);

  return useQuery({
    queryKey: ["ai-conversations", statusFilter, agentFilter, page, pageSize],
    queryFn: async () => {
      const response = await apiClient.get(
        `/ai/conversations${queryString ? `?${queryString}` : ""}`,
      );
      return {
        conversations: response?.result || [],
        pagination: response?.pagination || {},
      };
    },
    enabled: isEndpointEnabled("ai-conversations", "list"),
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false,
  });
};

/**
 * Hook to get current site info for an agent
 * @param {number} agentId - Agent ID
 * @returns {Object} Query result with current site data
 */
export const useAgentCurrentSite = (agentId) => {
  return useQuery({
    queryKey: getAgentSiteQueryKey(agentId),
    queryFn: async () => {
      const response = await apiClient.get(
        `/ai/agents/${agentId}/sites/current`,
      );
      return response?.result || null;
    },
    enabled: isEndpointEnabled("ai-agents", "get") && !!agentId,
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false,
    refetchInterval: (query) => {
      const processStatus = query?.state?.data?.ssl_process_status
        ?.toLowerCase()
        ?.trim();
      return processStatus === "pending" ? 5000 : false;
    },
  });
};

/**
 * Hook to create or update agent's site domain
 * @param {number} agentId - Agent ID
 * @returns {Object} Mutation result
 */
export const useUpdateAgentSite = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ agentId, domain }) => {
      const response = await apiClient.post(
        `/ai/agents/${agentId}/sites/current`,
        {
          domain,
        },
      );
      return response?.result;
    },
    onSuccess: (data, variables) => {
      syncAgentSiteCache(queryClient, variables.agentId, data);
      queryClient.invalidateQueries({ queryKey: ["ai-agents"] });
      queryClient.invalidateQueries({
        queryKey: getAgentSiteQueryKey(variables.agentId),
      });
    },
  });
};

/**
 * Hook to setup SSL certificate flow for agent's site
 * @returns {Object} Mutation result
 */
export const useSetupAgentSiteSSL = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ agentId }) => {
      const response = await apiClient.post(
        `/ai/agents/${agentId}/sites/current/ssl/setup`,
      );
      return response?.result;
    },
    onSuccess: (data, variables) => {
      syncAgentSiteCache(queryClient, variables.agentId, data);
      queryClient.invalidateQueries({ queryKey: ["ai-agents"] });
      queryClient.invalidateQueries({
        queryKey: getAgentSiteQueryKey(variables.agentId),
      });
    },
  });
};

/**
 * Hook to verify DNS configuration for agent's site
 * @returns {Object} Mutation result
 */
export const useVerifyAgentSiteSSL = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ agentId }) => {
      const response = await apiClient.post(
        `/ai/agents/${agentId}/sites/current/ssl/verify`,
      );
      return response?.result;
    },
    onSuccess: (data, variables) => {
      syncAgentSiteCache(queryClient, variables.agentId, data);
      queryClient.invalidateQueries({ queryKey: ["ai-agents"] });
      queryClient.invalidateQueries({
        queryKey: getAgentSiteQueryKey(variables.agentId),
      });
    },
  });
};

/**
 * Hook to re-issue SSL certificate for agent's site
 * @param {number} agentId - Agent ID
 * @returns {Object} Mutation result
 */
export const useReissueSSL = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ agentId }) => {
      const response = await apiClient.post(
        `/ai/agents/${agentId}/sites/current/ssl/re-issue`,
      );
      return response?.result;
    },
    onSuccess: (data, variables) => {
      syncAgentSiteCache(queryClient, variables.agentId, data);
      queryClient.invalidateQueries({ queryKey: ["ai-agents"] });
      queryClient.invalidateQueries({
        queryKey: getAgentSiteQueryKey(variables.agentId),
      });
    },
  });
};

/**
 * Hook to delete custom domain and reset SSL state
 * @returns {Object} Mutation result
 */
export const useDeleteAgentSiteSSL = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ agentId }) => {
      const response = await apiClient.post(
        `/ai/agents/${agentId}/sites/current/ssl/delete`,
      );
      return response?.result;
    },
    onSuccess: (data, variables) => {
      syncAgentSiteCache(queryClient, variables.agentId, data);
      queryClient.invalidateQueries({ queryKey: ["ai-agents"] });
      queryClient.invalidateQueries({
        queryKey: getAgentSiteQueryKey(variables.agentId),
      });
    },
  });
};

/**
 * Hook to initialize WhatsApp OAuth flow (Embedded Signup)
 * @returns {Object} Mutation result with facebook config for FB.login()
 */
export const useInitWhatsAppOAuth = () => {
  return useMutation({
    mutationFn: async ({ agentId, authStrategy }) => {
      const response = await apiClient.post(
        `/ai/agents/${agentId}/auth/business_login/init`,
        { auth_strategy: authStrategy },
      );
      return response?.result;
    },
  });
};

/**
 * Hook to complete WhatsApp OAuth flow by sending the authorization code
 * @returns {Object} Mutation result
 */
export const useCompleteWhatsAppOAuth = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ agentId, code, phone_number_id, waba_id }) => {
      const body = { code };
      if (phone_number_id) body.phone_number_id = phone_number_id;
      if (waba_id) body.waba_id = waba_id;

      const response = await apiClient.post(
        `/ai/agents/${agentId}/auth/business_login/complete`,
        body,
      );
      return response?.result;
    },
    onSuccess: async (_, variables) => {
      await refetchAndUpdateAgent(
        queryClient,
        variables.agentId,
        "oauth-complete",
      );
    },
    onError: async (_, variables) => {
      await refetchAndUpdateAgent(
        queryClient,
        variables.agentId,
        "oauth-error",
      );
    },
  });
};

/**
 * Hook to disconnect WhatsApp OAuth (revoke token)
 * @returns {Object} Mutation result
 */
export const useDisconnectWhatsAppOAuth = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ agentId }) => {
      const response = await apiClient.post(
        `/ai/agents/${agentId}/auth/business_login/logout`,
      );
      return response?.result;
    },
    onSuccess: async (_, variables) => {
      await refetchAndUpdateAgent(
        queryClient,
        variables.agentId,
        "oauth-disconnect",
      );
    },
  });
};

/**
 * Hook to download the WhatsApp Business Login PIN
 * @returns {Object} Mutation result
 */
export const useDownloadWhatsAppPin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ agentId }) => {
      const response = await apiClient.get(
        `/ai/agents/${agentId}/auth/business_login/pin/download`,
        { responseType: "blob" },
      );

      const blob =
        response instanceof Blob
          ? response
          : new Blob([response], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `whatsapp_pin_${agentId}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return response;
    },
    onSuccess: async (_, variables) => {
      await refetchAndUpdateAgent(
        queryClient,
        variables.agentId,
        "pin-download",
      );
    },
  });
};

/**
 * Hook to verify WhatsApp Business registration status
 * @returns {Object} Mutation result
 */
export const useVerifyWhatsAppRegistration = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ agentId }) => {
      const response = await apiClient.post(
        `/ai/agents/${agentId}/auth/business_login/verify_registration`,
      );
      return response?.result;
    },
    onSuccess: (data, variables) => {
      // Update agent in the list cache immediately with the returned data
      queryClient.setQueriesData({ queryKey: ["ai-agents"] }, (oldData) => {
        if (!oldData?.agents) return oldData;
        return {
          ...oldData,
          agents: oldData.agents.map((agent) =>
            agent.id === variables.agentId
              ? {
                  ...agent,
                  configured: data.configured,
                  provider_settings: {
                    ...agent.provider_settings,
                    registration_status: data.registration_status,
                  },
                }
              : agent,
          ),
        };
      });

      // Invalidate to refetch full agent data from server
      queryClient.invalidateQueries({ queryKey: ["ai-agents"] });
    },
  });
};

/**
 * Hook to run the WhatsApp Business Login doctor (repair connection)
 * @returns {Object} Mutation result
 */
export const useDoctorWhatsApp = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ agentId }) => {
      const response = await apiClient.post(
        `/ai/agents/${agentId}/auth/business_login/doctor`,
      );
      return response;
    },
    onSuccess: async (_, variables) => {
      await refetchAndUpdateAgent(queryClient, variables.agentId, "doctor");
    },
  });
};

/**
 * Build the React Query key for an agent's WhatsApp templates list.
 * @param {number|string} agentId
 * @param {string} [status] Optional status filter (WhatsAppTemplateAccountStatus value).
 */
export const getAgentTemplatesQueryKey = (agentId, status) =>
  status
    ? ["ai-agent-templates", agentId, status]
    : ["ai-agent-templates", agentId];

/**
 * Hook to list WhatsApp message templates for an agent.
 * GET /ai/agents/{agentId}/templates?status=...
 * @param {number|string} agentId
 * @param {{ status?: string }} [options]
 */
export const useAgentTemplates = (agentId, { status } = {}) => {
  return useQuery({
    queryKey: getAgentTemplatesQueryKey(agentId, status),
    queryFn: async () => {
      const qs = status ? `?status=${encodeURIComponent(status)}` : "";
      const response = await apiClient.get(
        `/ai/agents/${agentId}/templates${qs}`,
      );
      return response?.result ?? [];
    },
    enabled: isEndpointEnabled("ai-agents", "get") && !!agentId,
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });
};

/**
 * Hook to refresh WhatsApp template statuses from Meta (no local mutations).
 * POST /ai/agents/{agentId}/templates/refresh
 */
export const useRefreshAgentTemplates = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ agentId }) => {
      const response = await apiClient.post(
        `/ai/agents/${agentId}/templates/refresh`,
      );
      return response?.result;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["ai-agent-templates", variables.agentId],
      });
    },
  });
};

/**
 * Hook to sync (create/update) WhatsApp templates against Meta for an agent.
 * POST /ai/agents/{agentId}/templates/sync
 * @param {{ agentId: number|string, deleteRemoved?: boolean }} variables
 */
export const useSyncAgentTemplates = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ agentId, deleteRemoved = false }) => {
      const response = await apiClient.post(
        `/ai/agents/${agentId}/templates/sync`,
        { delete_removed: deleteRemoved },
      );
      return response?.result;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["ai-agent-templates", variables.agentId],
      });
    },
  });
};

/**
 * Hook to send a marketing template message via WhatsApp Cloud API.
 * POST /ai/agents/{agentId}/templates/{templateId}/send
 * Only works for APPROVED + MARKETING templates.
 */
export const useSendAgentTemplate = () => {
  return useMutation({
    mutationFn: async ({ agentId, templateId, to, bodyParameters = [] }) => {
      const response = await apiClient.post(
        `/ai/agents/${agentId}/templates/${templateId}/send`,
        { to, body_parameters: bodyParameters },
      );
      return response?.result;
    },
  });
};

export default {
  useAiAgents,
  useAiAgent,
  useProviderSettingsSchemas,
  useAvailableProviders,
  useProviderSchema,
  useCreateAiAgent,
  useUpdateAiAgent,
  useAgentCurrentConversation,
  useAgentConversationMessages,
  useAiConversations,
  useAgentCurrentSite,
  useUpdateAgentSite,
  useSetupAgentSiteSSL,
  useVerifyAgentSiteSSL,
  useReissueSSL,
  useDeleteAgentSiteSSL,
  useInitWhatsAppOAuth,
  useCompleteWhatsAppOAuth,
  useDisconnectWhatsAppOAuth,
  useDownloadWhatsAppPin,
  useVerifyWhatsAppRegistration,
  useDoctorWhatsApp,
  useAgentTemplates,
  useRefreshAgentTemplates,
  useSyncAgentTemplates,
  useSendAgentTemplate,
};
